import React, { useEffect, useState } from "react";
import { TextField } from "../common/inputFiled";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import toast from "react-hot-toast";
import Cookies from "js-cookie";

const INITIAL_INPUTS = { country: "in", pincode: "", place_name: "" };

const HIDE_DIAL_CODE_CSS = `
    .loc-country-select .react-tel-input .country-list .dial-code { display: none !important; }
    .loc-country-select .react-tel-input .country-list .country-name { margin-right: 0 !important; }
    .loc-country-select .react-tel-input .country-list { width: 350px !important; max-height: 220px !important; border-radius: 12px !important; box-shadow: 0 8px 24px rgba(108,69,255,0.18) !important; border: 1px solid #E6E2FF !important; }
    .loc-country-select .react-tel-input .country-list .country { padding: 10px 16px !important; font-size: 14px !important; }
    .loc-country-select .react-tel-input .country-list .country:hover { background: #F3EEFF !important; }
    .loc-country-select .react-tel-input .country-list .country.highlight { background: #EDE9FF !important; }
    .loc-country-select .react-tel-input .country-list .search { padding: 8px 12px !important; }
    .loc-country-select .react-tel-input .country-list .search-box { width: 100% !important; border-radius: 8px !important; border: 1px solid #E6E2FF !important; padding: 6px 10px !important; font-size: 13px !important; outline: none !important; }
`;

const normalizeSearchResults = (apiRows) => {
    const results = [];
    apiRows.forEach((row) => {
        const rowPlaces = Array.isArray(row.places) ? row.places : [];
        if (rowPlaces.length > 0) {
            rowPlaces.forEach((place) => {
                results.push({
                    ...place,
                    city: row.city || place.city || place.place_name || "",
                    pin_code: row.pin_code || place.pin_code || "",
                    state: row.state || place.state || place.state_abbreviation || "",
                    region: row.state || place.state || place.state_abbreviation || "",
                });
            });
            return;
        }
        results.push({
            ...row,
            region: row.state || row.region || "",
        });
    });
    return results;
};

export const LocationModal = ({
    show,
    onClose,
    onSelect,
    initialPincode = "",
    initialPlaceName = "",
}) => {
    const token = Cookies.get("auth_token");
    const [inputs, setInputs] = useState(INITIAL_INPUTS);
    const [countryName, setCountryName] = useState("India");
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (!show) return;
        setInputs({
            country: "in",
            pincode: String(initialPincode || ""),
            place_name: String(initialPlaceName || ""),
        });
        setCountryName("India");
        setErrors({});
    }, [show, initialPincode, initialPlaceName]);

    const validate = () => {
        const errs = {};
        if (!inputs.pincode.trim()) errs.pincode = "Pincode is required";
        return errs;
    };

    const applyFirstResult = (place, pincode) => {
        onSelect({
            place,
            location: {
                city: place.city,
                state: place.state,
                pin_code: place.pin_code,
            },
            country: place.country || (inputs.country || "").toUpperCase(),
            post_code: place.pin_code || pincode,
        });
    };

    const resetState = () => {
        setInputs(INITIAL_INPUTS);
        setCountryName("India");
        setErrors({});
    };

    const handleClose = () => {
        resetState();
        onClose();
    };

    const handleSearch = async () => {
        const errs = validate();
        if (Object.keys(errs).length) {
            setErrors(errs);
            return;
        }
        setErrors({});
        setLoading(true);
        try {
            const pincode = inputs.pincode.trim();
            const payload = new URLSearchParams({
                pincode,
                name: inputs.place_name.trim(),
            });
            if (inputs.country?.trim()) {
                payload.append("country", inputs.country.trim().toLowerCase());
            }

            const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/location/search`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/x-www-form-urlencoded",
                    Authorization: `Bearer ${token}`,
                },
                body: payload.toString(),
            });

            const data = await res.json();
            const apiRows = Array.isArray(data.data) ? data.data : [];
            const normalizedPlaces = normalizeSearchResults(apiRows);

            if (data.status === "1" && normalizedPlaces.length > 0) {
                applyFirstResult(normalizedPlaces[0], pincode);
                resetState();
                onClose();
            } else {
                toast.dismiss();
                toast.error("No locations found");
            }
        } catch (err) {
            toast.dismiss();
            toast.error(err.message || "Search failed");
        } finally {
            setLoading(false);
        }
    };

    if (!show) return null;

    return (
        <div className="fixed inset-0 bg-black/40 flex justify-center items-center z-50">
            <div className="bg-white w-[420px] rounded-2xl shadow-lg p-5 relative">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold">Find Location by Pincode</h3>
                    <button className="text-gray-500 hover:text-gray-700" onClick={handleClose}>
                        ✕
                    </button>
                </div>

                <div className="space-y-3">
                    <div className="space-y-1 loc-country-select">
                        <style>{HIDE_DIAL_CODE_CSS}</style>
                        <label className="block text-xs font-medium input-lable my-3">Country</label>
                        <div
                            className={`flex items-center w-[99%] h-11 rounded-[13px] border bg-white px-3 gap-2 ${
                                errors.country
                                    ? "border-red-500"
                                    : "border-[#E6E2FF] shadow-[inset_0_0_0_1px_rgba(108,69,255,0.15)]"
                            }`}
                        >
                            <PhoneInput
                                country={inputs.country || "in"}
                                value=""
                                onChange={(_val, data) => {
                                    setInputs((p) => ({ ...p, country: data.countryCode }));
                                    setCountryName(data.name || "");
                                    if (errors.country) setErrors((p) => ({ ...p, country: null }));
                                }}
                                enableSearch
                                searchPlaceholder="Search country..."
                                inputClass="!hidden"
                                buttonClass="!border-none !bg-transparent !p-0 !static !h-auto"
                                containerClass="!w-auto !h-auto flex-shrink-0"
                            />
                            <span className="text-sm text-gray-800 font-medium flex-1 truncate">
                                {countryName || "India"}
                            </span>
                        </div>
                        {errors.country && <p className="text-red-500 text-xs mt-1">{errors.country}</p>}
                    </div>
                    <TextField
                        label="Pincode / Post Code"
                        type="input"
                        placeholder="Enter pincode or post code"
                        value={inputs.pincode}
                        onChange={(e) => setInputs((p) => ({ ...p, pincode: e.target.value }))}
                        error={errors.pincode}
                    />
                    {/* <TextField
                        label="Place Name"
                        placeholder="Enter place name"
                        value={inputs.place_name}
                        onChange={(e) => setInputs((p) => ({ ...p, place_name: e.target.value }))}
                    /> */}
                    <button
                        onClick={handleSearch}
                        disabled={loading}
                        className="w-full bg-[#6938EF] text-white rounded-full py-2.5 hover:brightness-110 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center submit-button"
                    >
                        {loading ? (
                            <>
                                <svg className="animate-spin mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                                </svg>
                                Searching...
                            </>
                        ) : (
                            "Search"
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};
